//@flow
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  Checkbox,
  Collapse,
  IconButton,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
} from '@material-ui/core';

import {KeyboardArrowDown, KeyboardArrowUp} from '@material-ui/icons';

import {VARIANT_TABLE_CELLS} from '../../constants';

import VariantRow from './VariantRow';

import {ProductVariantRequestProvider} from '../../context/productVariantRequest';
import {ProductVariantProvider} from '../../context/productVariant';
import {useProductContext} from '../../context';
import VariantTableHeader from './VariantTableHeader';
import ProductCellsList from './ProductCellsList';
import {
  useFeatureSpecLimit,
  useSelectedProducts,
  useSelectedProductsVariants,
  useToggleProductSelection,
  useToggleVariantsInProductSelection,
  useUpdateProductSelected,
} from '../../utils/bulkEditOptions';
import {useAlert} from '../../../../elements/Alert/AlertContext';
import {useIsFeatureInTier} from '../../../../utils/component/index';
import {classNames} from '../../../../utils/react';

const useStyles = makeStyles(theme => ({
  disabled: {
    opacity: 0,
  },
  variantTable: {
    background: '#F4F4F666',
  },

  tableBody: {
    '& th': {
      padding: '.75rem',
    },
  },
  rowBlocked: {
    pointerEvents: 'none',
  },
  row: {
    position: 'relative',
    '&[data-external-created=true]': {
      background: '#f4f4f699',
    },
  },
  externalCreationOverlay: {
    position: 'absolute',
    opacity: 0,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: '0 1rem',
    zIndex: 1,
    background: '#f4f4f699',
    transition: 'opacity 0.3s',
    '&:hover': {
      opacity: 1,
    },
  },
  chipContainer: {
    position: 'sticky',
    left: '.5rem',
  },
}));

function ProductRow() {
  const classes = useStyles();

  const [open, setOpen] = useState(false);
  const {showAlert} = useAlert();
  const hasBulkEditFeature = useIsFeatureInTier('PRODUCTS_BULK_EDIT');
  const limitedSelected = useFeatureSpecLimit();

  const {updateProductSelected} = useUpdateProductSelected();
  const toggleProductSelected = useToggleProductSelection();
  const toggleVariantsInProduct = useToggleVariantsInProductSelection();
  const selectedProducts = useSelectedProducts();
  const productsVariantsSelection = useSelectedProductsVariants();

  const [product] = useProductContext(product => {
    const taxes = product.taxes ?? [];
    const categoryTaxes = product.categoryTaxes ?? [];

    return {
      id: product.id,
      productId: product.id,
      price: product.taxedPrice,
      offerPrice: product.taxedOfferPrice,
      // taxedPrice: product.taxedPrice,
      // offerPrice: product.offerPrice,
      stock: product?.stockSetting?.stock,
      stockSetting: product?.stockSetting,
      categories: product.categories,
      enabledProductVariant: !!product.productVariantSetting?.enabled,
      variants: product.productVariantSetting?.variants,
      bulkPriceSetting: product.bulkPriceSetting,
      options: product.productVariantSetting?.options || [],
      metadataSettings: product.metadataSetting,
      taxFactor: [...taxes, ...categoryTaxes]
        .map(parseFloat)
        .reduce((a, b) => a + b, 1),
    };
  });

  // const isFromExternalResource = !!product?.metadataSettings?.source;
  const isFromExternalResource = false;
  const taxFactor = product.taxFactor;

  // const calcPriceWithTax = useCallback(
  //   (value: number) => {
  //     return parseFloat(value * taxFactor ?? 1).toFixed(5);
  //   },
  //   [taxFactor]
  // );

  const calcPriceWithoutTax = useCallback(
    (value: number) => {
      return parseFloat(value / taxFactor ?? 1).toFixed(5);
    },
    [taxFactor]
  );

  const toggleVariantsProduct = useCallback(() => {
    if (product.variants && product.variants.length > 0) {
      toggleVariantsInProduct(
        {
          ...product,
          productVariantSetting: {
            variants: product.variants,
          },
        },
        limitedSelected
      );
    }
  }, [toggleVariantsInProduct, product, limitedSelected]);

  const hasVariants =
    product.enabledProductVariant &&
    product.variants &&
    product.variants.length > 0;

  const productVariants = useMemo(() => {
    return (product.variants ?? []).map(v => ({
      ...v,
      price: calcPriceWithoutTax(Number(v.price)),
      taxedPrice: v.price || '',
      offerPrice: v.offerPrice ? calcPriceWithoutTax(Number(v.offerPrice)) : '',
      taxedOfferPrice: v.offerPrice || '',
    }));
  }, [calcPriceWithoutTax, product.variants]);

  const checkedProduct = useMemo(() => {
    return selectedProducts.some(
      productSelected => productSelected.id === product.id
    );
  }, [product.id, selectedProducts]);

  useEffect(() => {
    if (!checkedProduct) return;
    updateProductSelected(product);
  }, [product, checkedProduct, productVariants, updateProductSelected]);

  return (
    <>
      <TableRow
        tabIndex={-1}
        className={classNames(classes.row, {
          [classes.rowBlocked]: !hasBulkEditFeature,
        })}
        data-external-created={isFromExternalResource}
      >
        <TableCell padding="checkbox">
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => {
              if (hasVariants) setOpen(!open);
            }}
            disabled={!hasVariants}
            className={!hasVariants ? classes.disabled : ''}
          >
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell padding="checkbox">
          <Checkbox
            onChange={() => {
              const isSelected = selectedProducts.some(
                selectedProduct => selectedProduct.id === product.id
              );
              const isOverLimit =
                selectedProducts.length + productsVariantsSelection.length >=
                limitedSelected;
              if (isOverLimit && isSelected) {
                toggleProductSelected(product);
                return;
              }
              if (isOverLimit) {
                showAlert(
                  isOverLimit
                    ? `Solo puedes seleccionar ${limitedSelected} productos/variantes`
                    : 'Este producto ya está seleccionado',
                  {severity: 'warning'}
                );
                return;
              }
              toggleProductSelected(product);
              toggleVariantsProduct();
            }}
            checked={checkedProduct}
            color="primary"
          />
        </TableCell>
        <ProductCellsList />
        {isFromExternalResource && (
          <div className={classes.externalCreationOverlay}>
            <span className={classes.chipContainer}>
              <Chip
                label="Este producto fue creado desde una fuente externa y no puede ser
                editado"
                color="primary"
              />
            </span>
          </div>
        )}
      </TableRow>
      {hasVariants && (
        <TableRow>
          <ProductVariantRequestProvider
            productId={product.id}
            enabledMixAndMatch={
              product.bulkPriceSetting &&
              product.bulkPriceSetting.enabledMixAndMatch
            }
            options={product.options}
            variants={productVariants}
          >
            <TableCell
              style={{
                paddingBottom: 0,
                paddingTop: 0,
                paddingLeft: '3rem',
                borderBottom: 'none',
              }}
              colSpan={VARIANT_TABLE_CELLS.length - 2}
            >
              <Collapse in={open} timeout="auto" unmountOnExit>
                <Table className={classes.variantTable}>
                  <TableHead>
                    <VariantTableHeader />
                  </TableHead>
                  <TableBody className={classes.tableBody}>
                    {/* {product.variants &&
                      product.variants.map(productVariant => (
                        <ProductVariantProvider
                          key={productVariant.id}
                          productVariantId={productVariant.id}
                        >
                          <VariantRow
                            productId={product.id}
                            variant={productVariant}
                            countProductSelected={selectedProducts.length}
                            limitedSelected={limitedSelected}
                          />
                        </ProductVariantProvider>
                      ))} */}
                    {productVariants.map(productVariant => (
                      <ProductVariantProvider
                        key={productVariant.id}
                        productVariantId={productVariant.id}
                      >
                        <VariantRow
                          productId={product.id}
                          variant={productVariant}
                          countProductSelected={selectedProducts.length}
                          limitedSelected={limitedSelected}
                        />
                      </ProductVariantProvider>
                    ))}
                  </TableBody>
                </Table>
              </Collapse>
            </TableCell>
          </ProductVariantRequestProvider>
        </TableRow>
      )}
    </>
  );
}

export default React.memo<any>(ProductRow);
